import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import { useEffect, useState } from 'react'
import { getCurrentUser, signInWithRedirect } from 'aws-amplify/auth'
import { Authenticator } from '@aws-amplify/ui-react'
import '@aws-amplify/ui-react/styles.css'
import Dashboard from './components/Dashboard'
import Login from './components/Login'
import Unauthorized from './components/Unauthorized'
import './App.css'

// Configure Amplify from environment
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID as string
const USER_POOL_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string
let COGNITO_DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN as string) || ''
// Allow either full URL or bare domain in env
if (COGNITO_DOMAIN.startsWith('https://')) {
  COGNITO_DOMAIN = COGNITO_DOMAIN.replace('https://', '')
}

const ORIGIN = window.location.origin

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: COGNITO_DOMAIN, // e.g., yourprefix.auth.us-east-1.amazoncognito.com
          scopes: ['email', 'profile', 'openid'],
          redirectSignIn: [ORIGIN],
          redirectSignOut: [ORIGIN],
          responseType: 'code'
        }
      }
    }
  }
})

// Auto-redirect to Google IdP when not authenticated (no manual login screen)
function useAutoFederatedLogin() {
  const [isUnauthorized, setIsUnauthorized] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        await getCurrentUser()
        setIsChecking(false)
      } catch (error: any) {
        // Check if error indicates unauthorized/restricted access
        if (error?.message?.includes('restricted') || error?.message?.includes('not authorized')) {
          setIsUnauthorized(true)
          setIsChecking(false)
        } else {
          // Not authenticated yet, redirect to Google
          try {
            await signInWithRedirect({ provider: 'Google' })
          } catch (e) {
            console.warn('Federated sign-in redirect failed', e)
            // If redirect fails, might be unauthorized
            setIsUnauthorized(true)
            setIsChecking(false)
          }
        }
      }
    })()
  }, [])

  return { isUnauthorized, isChecking }
}

function App() {
  const { isUnauthorized, isChecking } = useAutoFederatedLogin()

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🔐</div>
          <p className="text-gray-600">Checking authorization...</p>
        </div>
      </div>
    )
  }

  // Show unauthorized page if user is not authorized
  if (isUnauthorized) {
    return <Unauthorized />
  }

  return (
    <Authenticator.Provider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={
              <Authenticator>
                <Dashboard />
              </Authenticator>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
          </Routes>
        </div>
      </Router>
    </Authenticator.Provider>
  )
}

export default App
